import { useCallback, useEffect } from 'react';
import { useNavigate, type NavigateFunction, type NavigateOptions, type To } from 'react-router-dom';

/**
 * Warnung vor dem Verlassen einer Seite mit ungesicherten Aenderungen.
 *
 * Zwei Wege fuehren aus einer Seite heraus, und beide muessen abgefangen werden:
 *
 * 1. Browser schliessen oder neu laden → `beforeunload`.
 * 2. Navigation innerhalb der App. Die App haengt an `<BrowserRouter>`, also
 *    ohne Data-Router — `useBlocker` gibt es hier nicht. Stattdessen gibt es
 *    eine kleine Wache im Modul: die Seite traegt sich ein, solange sie
 *    ungesicherte Aenderungen hat, und die Stellen, die wirklich wegnavigieren,
 *    fragen vorher nach. Das sind die Navigationsleisten (ueber
 *    `useNavigateGeschuetzt`) und jeder `<Link>` (ueber den Klick-Abfang in
 *    der Capture-Phase, bevor der Router den Klick sieht).
 *
 * Nicht abgedeckt ist der Zurueck-Knopf des Browsers innerhalb der App: dabei
 * feuert weder `beforeunload` noch ein Klick, und ein Rueckgaengigmachen ueber
 * die History wuerde die Seite neu aufbauen und die Eingaben erst recht
 * verlieren. Lieber ehrlich eine Luecke als eine Kruecke, die Daten frisst.
 */

let wache: (() => boolean) | null = null;

/**
 * Darf gerade weggegangen werden? Ohne eingetragene Wache immer ja.
 * Die Wache selbst fragt den Nutzer und gibt true, wenn er verwerfen will.
 */
export function darfVerlassen(): boolean {
  return wache ? wache() : true;
}

/** Nur fuer Tests und Notfaelle: Wache abraeumen. */
export function verlassenWacheZuruecksetzen(): void {
  wache = null;
}

export const VERLASSEN_FRAGE =
  'Es gibt Aenderungen, die noch nicht gesichert sind. Seite verlassen und Aenderungen verwerfen?';

/**
 * Meldet die Seite an, solange `aktiv` true ist.
 * `frage` ist austauschbar, damit Tests nicht am globalen confirm haengen.
 */
export function useVerlassenWarnung(aktiv: boolean, frage: string = VERLASSEN_FRAGE): void {
  useEffect(() => {
    if (!aktiv) return;

    const erlauben = () => window.confirm(frage);
    wache = erlauben;

    const beimSchliessen = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Aeltere Browser zeigen den Dialog nur, wenn returnValue gesetzt ist.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', beimSchliessen);

    const beimKlick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return; // neuer Tab: Seite bleibt
      const ziel = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!ziel) return;
      const fenster = ziel.getAttribute('target');
      if (fenster && fenster !== '_self') return;
      const href = ziel.getAttribute('href') ?? '';
      // Nur App-interne Ziele. Externe Adressen verlassen das Dokument, da
      // greift beforeunload.
      if (!href.startsWith('/')) return;
      if (href === window.location.pathname + window.location.search) return;
      if (erlauben()) return;
      e.preventDefault();
      e.stopPropagation();
    };
    document.addEventListener('click', beimKlick, true);

    return () => {
      if (wache === erlauben) wache = null;
      window.removeEventListener('beforeunload', beimSchliessen);
      document.removeEventListener('click', beimKlick, true);
    };
  }, [aktiv, frage]);
}

/**
 * Wie `useNavigate`, fragt aber vorher die Wache. Die Navigationsleisten
 * navigieren programmatisch statt ueber `<Link>`, deshalb reicht der
 * Klick-Abfang dort nicht.
 */
export function useNavigateGeschuetzt(): NavigateFunction {
  const navigate = useNavigate();
  return useCallback(
    ((to: To | number, options?: NavigateOptions) => {
      if (!darfVerlassen()) return;
      if (typeof to === 'number') return navigate(to);
      return navigate(to, options);
    }) as NavigateFunction,
    [navigate],
  );
}
